/**
 * "Sıradaki içerik" kancası.
 *
 * ## Kural kimde?
 *
 * Sıradaki içeriğin ne olduğuna **sunucu** karar verir (müzik → sıradaki
 * parça, dizi → sıradaki bölüm). İstemci yalnız sonucu taşır; kendi
 * kopyasını tutmaz — iki kural birbirinden ayrışırsa oynatıcı yanlış içeriği
 * açabilir.
 *
 * ## Neden ayrı kanca?
 *
 * Oynatıcı ekranı zaten büyük (istatistik, tercihler, altyazı, raporlama).
 * "Sıradaki" mantığı buraya gömülürse, izleme ekranındaki her değişiklik bu
 * kısmı da etkiler. Ayrı kanca: tek sorumluluk, test edilebilir, izole.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, type Item } from './api.ts';

export interface NextItemState {
  /** Sıradaki öğe; yoksa `null`. */
  next: Item | null;
  /**
   * Sıradaki öğenin anlamı.
   *
   * `track` = müzik parçası (bitince otomatik sıradakine geçilir),
   * `episode` = bölüm (kullanıcı "sıradaki bölüm" düğmesine basar).
   */
  relation: 'track' | 'episode' | null;
  /** Sıradaki öğe var mı **ve** geçiş yapılabiliyor mu? */
  hasNext: boolean;
  /** Sorgu sürüyor mu? */
  isLoading: boolean;
  /** Sıradaki içeriğe geç. */
  playNext: () => void;
}

/**
 * Verilen öğenin ardından oynanacak içeriği getirir.
 *
 * @param itemId      Oynatılan öğenin kimliği; yoksa sorgu yapılmaz.
 * @param onPlayNext  Sıradakine geçiş çağrısı. Verilmezse düğme **pasif**
 *                    kalır (yönlendirme bu katmanın işi değildir).
 */
export function useNextItem(
  itemId: string | null | undefined,
  onPlayNext?: ((itemId: string) => void) | undefined,
): NextItemState {
  const [next, setNext] = useState<Item | null>(null);
  const [relation, setRelation] = useState<'track' | 'episode' | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!itemId) {
      setNext(null);
      setRelation(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    // Önceki öğenin "sıradakisi" **anında** temizlenir: yeni öğe için geçerli
    // olmayan bir başlık düğmenin üzerinde bir saniye daha görünürdü.
    setNext(null);
    setRelation(null);
    setIsLoading(true);

    void api
      .nextItem(itemId)
      .then((result) => {
        if (cancelled) return;
        setNext(result.item);
        setRelation(result.relation);
      })
      .catch(() => {
        // Hata "sıradaki yok" gibi davranılır: oynatıcı bozulmaz.
        if (cancelled) return;
        setNext(null);
        setRelation(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [itemId]);

  const playNext = useCallback(() => {
    if (next === null) return;
    onPlayNext?.(next.id);
  }, [next, onPlayNext]);

  return {
    next,
    relation,
    // Geçiş yapılamıyorsa (yönlendirme yok) düğme pasif kalır.
    hasNext: next !== null && onPlayNext !== undefined,
    isLoading,
    playNext,
  };
}
